import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Download, Loader2, AlertTriangle, FolderOpen, HardDrive, CheckCircle, Sparkles, Cloud, Layers } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { detectMimeType, getExtensionFromMime } from '@/utils/imageUtils';
import { useBackgroundDownload } from '@/hooks/useBackgroundDownload';

interface Job {
  id: string;
  job_name: string;
  images_generated: number;
  downloaded_at: string | null;
}

interface OrganizedDownloadSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobs: Job[];
  onJobsDownloaded?: (jobIds: string[]) => Promise<void>;
}

const SOFT_LIMIT = 500; // Show warning
const HARD_LIMIT = 2000; // Prevent download for browser download
const BACKGROUND_THRESHOLD = 100; // Suggest background download above this
const AVG_IMAGE_SIZE_MB = 0.4; // ~400KB per image estimate
const BATCH_SIZE = 5; // Smaller batches for memory efficiency

export const OrganizedDownloadSelector = ({
  open,
  onOpenChange,
  jobs,
  onJobsDownloaded
}: OrganizedDownloadSelectorProps) => {
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, job: '' });
  const [actualCounts, setActualCounts] = useState<Record<string, number>>({});
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [folderMode, setFolderMode] = useState<'by-job' | 'flat'>('by-job');
  
  const { startBackgroundDownload, loading: backgroundLoading } = useBackgroundDownload();

  // Helper to get real image count (from RPC or fallback to job.images_generated)
  const getImageCount = (jobId: string) => actualCounts[jobId] ?? 0;

  const jobsWithImages = jobs.filter(j => getImageCount(j.id) > 0 || j.images_generated > 0);

  // Calculate totals using actual counts
  const selectedJobsList = jobsWithImages.filter(j => selectedJobs.has(j.id));
  const totalImages = selectedJobsList.reduce((sum, j) => sum + getImageCount(j.id), 0);
  const estimatedSizeMB = Math.round(totalImages * AVG_IMAGE_SIZE_MB);

  // Reset selection only when dialog opens (not when jobs change)
  useEffect(() => {
    if (open) {
      setSelectedJobs(new Set());
      setProgress({ current: 0, total: 0, job: '' });
    }
  }, [open]);

  // Fetch actual image counts when dialog opens or jobs change
  useEffect(() => {
    if (open && jobs.length > 0) {
      setLoadingCounts(true);
      
      const fetchCounts = async () => {
        const jobIds = jobs.map(j => j.id);
        const { data, error } = await supabase
          .rpc('get_actual_image_counts', { job_ids: jobIds });
        
        if (!error && data) {
          const counts: Record<string, number> = {};
          (data as { job_id: string; image_count: number }[]).forEach((row) => {
            counts[row.job_id] = row.image_count;
          });
          setActualCounts(counts);
        } else {
          // Fallback to job.images_generated if RPC fails
          const fallbackCounts: Record<string, number> = {};
          jobs.forEach(j => {
            fallbackCounts[j.id] = j.images_generated;
          });
          setActualCounts(fallbackCounts);
        }
        setLoadingCounts(false);
      };
      fetchCounts();
    }
  }, [open, jobs]);

  const toggleJob = (jobId: string) => {
    const newSelected = new Set(selectedJobs);
    if (newSelected.has(jobId)) {
      newSelected.delete(jobId);
    } else {
      newSelected.add(jobId);
    }
    setSelectedJobs(newSelected);
  };

  const selectAll = () => {
    setSelectedJobs(new Set(jobsWithImages.map(j => j.id)));
  };

  const selectNotDownloaded = () => {
    const notDownloadedIds = jobsWithImages
      .filter(j => !j.downloaded_at)
      .map(j => j.id);
    setSelectedJobs(new Set(notDownloadedIds));
  };

  const clearAll = () => {
    setSelectedJobs(new Set());
  };

  const notDownloadedCount = jobsWithImages.filter(j => !j.downloaded_at).length;

  const selectOldestWithinLimit = () => {
    // Sort jobs by created_at (oldest first) - jobs array comes from parent
    const sorted = [...jobsWithImages].sort((a, b) => {
      // Jobs without downloaded_at first, then by position (oldest first)
      return 0; // maintain original order which is already chronological
    });
    const selected = new Set<string>();
    let runningTotal = 0;
    for (const job of sorted) {
      const count = getImageCount(job.id);
      if (runningTotal + count <= HARD_LIMIT) {
        selected.add(job.id);
        runningTotal += count;
      } else {
        break;
      }
    }
    setSelectedJobs(selected);
    toast.info(`Auto-selected ${selected.size} jobs (${runningTotal} images) within ${HARD_LIMIT} limit`);
  };

  const handleDownload = async () => {
    if (totalImages > HARD_LIMIT) {
      toast.error(`Too many images (${totalImages}). Maximum is ${HARD_LIMIT}. Please select fewer jobs.`);
      return;
    }

    if (selectedJobs.size === 0) {
      toast.error('Please select at least one job');
      return;
    }

    setDownloading(true);
    const toastId = toast.loading('Preparing organized download...');

    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      let totalSuccess = 0;
      let processedImages = 0;

      // Dynamic batch size and GC pause based on total images
      const DYNAMIC_BATCH_SIZE = totalImages > 50 ? 3 : 5;
      const GC_PAUSE = totalImages > 50 ? 100 : 50;

      setProgress({ current: 0, total: totalImages, job: '' });

      // Flat mode: all images in root, By-job mode: each job in its own folder
      const isFlat = folderMode === 'flat';
      let globalImageCount = 0;

      for (const job of selectedJobsList) {
        const folderName = job.job_name
          .replace(/^\d+[.\s-]+/, '')
          .replace(/[–—]/g, '-')
          .replace(/[^a-zA-Z0-9_-]/g, '_');
        
        const targetFolder = isFlat ? zip : zip.folder(folderName);
        setProgress(p => ({ ...p, job: job.job_name }));

        const { data: imageIds, error: idsError } = await supabase
          .from('generated_images')
          .select('id')
          .eq('automated_job_id', job.id);

        if (idsError || !imageIds?.length) {
          console.warn(`Failed to get images for job ${job.job_name}:`, idsError);
          continue;
        }

        let jobImageCount = 0;

        for (let i = 0; i < imageIds.length; i += DYNAMIC_BATCH_SIZE) {
          await new Promise(r => requestAnimationFrame(() => r(undefined)));
          
          const batchIds = imageIds.slice(i, i + DYNAMIC_BATCH_SIZE).map(img => img.id);
          
          const { data: batchImages, error: batchError } = await supabase
            .from('generated_images')
            .select('id, b64_json')
            .in('id', batchIds);

          if (batchError) {
            console.warn(`Batch fetch error:`, batchError);
            continue;
          }

          for (const image of batchImages || []) {
            try {
              if (!image.b64_json || image.b64_json.length < 100) continue;

              const mimeType = detectMimeType(image.b64_json);
              const ext = getExtensionFromMime(mimeType);
              
              const binaryString = atob(image.b64_json);
              const bytes = new Uint8Array(binaryString.length);
              for (let j = 0; j < binaryString.length; j++) {
                bytes[j] = binaryString.charCodeAt(j);
              }

              const fileName = isFlat
                ? `image_${++globalImageCount}.${ext}`
                : `${folderName}_${++jobImageCount}.${ext}`;
              targetFolder?.file(fileName, bytes);
              totalSuccess++;
              processedImages++;

              (image as any).b64_json = null;
            } catch (err) {
              console.error('Failed to process image:', err);
            }
          }

          if (batchImages?.length) (batchImages as any).length = 0;

          setProgress(p => ({ ...p, current: processedImages }));
          toast.loading(`Processing: ${processedImages}/${totalImages} images...`, { id: toastId });

          await new Promise(r => setTimeout(r, GC_PAUSE));
        }
      }

      if (totalSuccess === 0) {
        toast.error('No valid images found to download', { id: toastId });
        return;
      }

      toast.loading('Generating ZIP file...', { id: toastId });

      // Use streaming progress callback to prevent UI freeze
      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 1 },
        streamFiles: true
      }, (metadata) => {
        if (metadata.percent % 5 < 1) {
          toast.loading(`Compressing: ${Math.round(metadata.percent)}%`, { id: toastId });
        }
      });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `jobs_organized_${new Date().toISOString().split('T')[0]}.zip`;
      link.click();
      URL.revokeObjectURL(link.href);

      // Mark jobs as downloaded
      if (onJobsDownloaded) {
        try {
          await onJobsDownloaded(Array.from(selectedJobs));
        } catch (err) {
          console.error('Failed to mark jobs as downloaded:', err);
        }
      }

      const folderMsg = isFlat ? '1 flat folder' : `${selectedJobsList.length} folders`;
      toast.success(`Downloaded ${totalSuccess} images in ${folderMsg}`, { id: toastId });
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating organized ZIP:', error);
      toast.error('Failed to create ZIP. Try selecting fewer jobs.', { id: toastId });
    } finally {
      setDownloading(false);
      setProgress({ current: 0, total: 0, job: '' });
    }
  };

  const isOverSoftLimit = totalImages > SOFT_LIMIT;
  const isOverHardLimit = totalImages > HARD_LIMIT;
  const suggestBackground = totalImages > BACKGROUND_THRESHOLD;

  const handleBackgroundDownload = async () => {
    const jobIds = Array.from(selectedJobs);
    const result = await startBackgroundDownload(jobIds);
    if (result) {
      if (onJobsDownloaded) {
        await onJobsDownloaded(jobIds);
      }
      onOpenChange(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-background border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            Download Jobs as Organized ZIP
          </DialogTitle>
          <DialogDescription>
            Select jobs and choose how to organize the downloaded files.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Summary Stats */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold">{selectedJobs.size}</div>
                <div className="text-xs text-muted-foreground">Jobs</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{totalImages}</div>
                <div className="text-xs text-muted-foreground">Images</div>
              </div>
              <div className="text-center flex items-center gap-1">
                <HardDrive className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-lg font-bold">~{estimatedSizeMB} MB</div>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAll}>
                Select All
              </Button>
              {notDownloadedCount > 0 && (
                <Button variant="outline" size="sm" onClick={selectNotDownloaded} className="text-primary">
                  <Sparkles className="h-3 w-3 mr-1" />
                  New ({notDownloadedCount})
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={selectOldestWithinLimit} className="text-primary" title={`Auto-select oldest jobs up to ${HARD_LIMIT} images`}>
                Max {HARD_LIMIT}
              </Button>
              <Button variant="ghost" size="sm" onClick={clearAll}>
                Clear
              </Button>
            </div>
          </div>

          {/* Warning Messages */}
          {isOverHardLimit && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive">
              <AlertTriangle className="h-5 w-5 flex-shrink-0" />
              <div className="text-sm">
                <strong>Too many images for browser download!</strong> Use background download instead.
              </div>
            </div>
          )}

          {suggestBackground && !isOverHardLimit && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/10 border border-primary/30 text-primary">
              <Cloud className="h-5 w-5 flex-shrink-0" />
              <div className="text-sm">
                <strong>Tip:</strong> For {totalImages}+ images, use Background Download to avoid browser freezing.
              </div>
            </div>
          )}

          {isOverSoftLimit && !isOverHardLimit && !suggestBackground && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-600">
              <AlertTriangle className="h-5 w-5 flex-shrink-0" />
              <div className="text-sm">
                <strong>Large download:</strong> This may take several minutes and use significant memory.
              </div>
            </div>
          )}

          {/* Folder Structure Option */}
          <div className="flex items-center gap-4 p-3 rounded-lg bg-muted/30 border">
            <Label className="text-sm font-medium whitespace-nowrap">Structure:</Label>
            <RadioGroup
              value={folderMode}
              onValueChange={(v: 'by-job' | 'flat') => setFolderMode(v)}
              className="flex gap-4"
            >
              <div className="flex items-center gap-1.5">
                <RadioGroupItem value="by-job" id="by-job" />
                <Label htmlFor="by-job" className="text-sm font-normal flex items-center gap-1 cursor-pointer">
                  <FolderOpen className="h-3.5 w-3.5" />
                  By Job
                </Label>
              </div>
              <div className="flex items-center gap-1.5">
                <RadioGroupItem value="flat" id="flat" />
                <Label htmlFor="flat" className="text-sm font-normal flex items-center gap-1 cursor-pointer">
                  <Layers className="h-3.5 w-3.5" />
                  All in One Folder
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Job List */}
          <ScrollArea className="h-[260px] rounded-md border p-2">
            <div className="space-y-2">
              {jobsWithImages.map(job => (
                <label
                  key={job.id}
                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedJobs.has(job.id)
                      ? 'bg-primary/10 border border-primary/30'
                      : 'bg-muted/30 border border-transparent hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={selectedJobs.has(job.id)}
                      onCheckedChange={() => toggleJob(job.id)}
                    />
                    <div>
                      <div className="font-medium text-sm flex items-center gap-2">
                        {job.job_name}
                        {job.downloaded_at && (
                          <Badge variant="outline" className="text-green-600 border-green-600 text-[10px] px-1.5 py-0">
                            <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
                            Downloaded
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {loadingCounts ? '...' : getImageCount(job.id)} images • ~{Math.round(getImageCount(job.id) * AVG_IMAGE_SIZE_MB)} MB
                      </div>
                    </div>
                  </div>
                  <Badge variant="secondary">{loadingCounts ? '...' : getImageCount(job.id)}</Badge>
                </label>
              ))}
            </div>
          </ScrollArea>

          {/* Download Progress */}
          {downloading && progress.total > 0 && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Processing: {progress.job}</span>
                <span>{progress.current}/{progress.total}</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={downloading || backgroundLoading}>
            Cancel
          </Button>
          
          {/* Browser Download Button (secondary - requires tab open) */}
          <Button 
            variant="outline"
            onClick={handleDownload} 
            disabled={downloading || backgroundLoading || selectedJobs.size === 0 || isOverHardLimit}
            title="Downloads in browser - tab must stay open"
          >
            {downloading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Browser Download
              </>
            )}
          </Button>

          {/* Background Download Button (primary - works even when tab is closed) */}
          <Button 
            onClick={handleBackgroundDownload} 
            disabled={downloading || backgroundLoading || selectedJobs.size === 0}
          >
            {backgroundLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <Cloud className="h-4 w-4 mr-2" />
                Download
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
